import { AuthenticationClient } from '@src/authentication/AuthenticationClient';
import { AuthenticationException } from '@src/authentication/exceptions/AuthenticationException';
import { MustChangePasswordException } from '@src/authentication/exceptions/MustChangePasswordException';
import { LogoAndTitle } from '@src/common/components/brand/LogoAndTitle';
import { TruckPanel } from '@src/common/components/brand/TruckPanel';
import { Button } from '@src/common/components/button/Button';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { Navigation } from '@src/routing/Navigation';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Location } from 'react-router-dom';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';

type FormInputs = {
  email: string;
  password: string;
};

export const LoginPage = () => {
  const { t } = useTranslation();
  const {
    register,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormInputs>();
  const { email, password } = watch();
  const navigate = useNavigate();
  const useFromLocation = () => {
    const location = useLocation();
    return (location.state as { from?: Location } | null)?.from;
  };
  const from = useFromLocation();

  const login: SubmitHandler<FormInputs> = async ({ email, password }) => {
    try {
      await AuthenticationClient.login({
        email,
        password,
      });
      navigate(from ?? OrdersNavigation.ACTIVE_ASSIGNED_TO_ME_ORDER_PATH, {
        replace: true,
      });
    } catch (e) {
      if (e instanceof AuthenticationException) {
        setError('root', { message: t(`authenticationErrors.${e.errorCode}`) });
      }
      if (e instanceof MustChangePasswordException) {
        navigate({
          pathname: Navigation.changeTemporaryPassword,
          search: createSearchParams({
            email,
          }).toString(),
        });
      }
    }
  };

  return (
    <PageLayout>
      <Stack
        className={cardContent}
        spacing={{ xs: theme.spacing['6'], md: theme.spacing['8'] }}
      >
        <LogoAndTitle width={136} />
        <Typography
          style={{ paddingTop: theme.spacing['6'] }}
          component="h1"
          variant="h3"
        >
          {t('login')}
        </Typography>
        <Stack spacing={{ md: theme.spacing['1'] }}>
          <Typography
            style={theme.typography.body.large}
          >{`${t('welcomeBack')}!`}</Typography>
          <Typography style={theme.typography.body.large}>
            {t('loginDescription')}
          </Typography>
        </Stack>
        <form onSubmit={handleSubmit(login)}>
          <Stack width={{ xs: '272px', md: '310px' }}>
            <Stack gap={theme.spacing['8']} marginBottom={theme.spacing['4']}>
              <Stack gap={theme.spacing['4']}>
                <TextInput
                  label={t('email')}
                  {...register('email', { required: true })}
                  error={errors.email && t(`formErrors.isRequired`)}
                />

                <TextInput
                  label={t('password')}
                  type="password"
                  {...register('password', { required: true })}
                  error={errors.password && t(`formErrors.isRequired`)}
                />
              </Stack>

              <Stack direction="row" justifyContent="center">
                <Button
                  className={loginButton}
                  variant="contained"
                  type="submit"
                  disabled={!email || !password}
                >
                  {t('login')}
                </Button>
              </Stack>

              {errors.root && (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography className={error}>
                    {errors.root.message}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Stack direction="row" justifyContent="center">
              <Button
                variant="text"
                onClick={() => navigate(Navigation.forgotPassword)}
              >
                {t('forgotPassword.button')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Stack>
    </PageLayout>
  );
};

type Props = {
  children?: React.ReactNode;
};

const PageLayout = ({ children }: Props) => {
  return (
    <div className={page}>
      <Stack className={content}>{children}</Stack>
      <div style={{ display: 'flex', flex: 3 }}>
        <TruckPanel />
      </div>
    </div>
  );
};

const page = css(() => ({
  height: '100vh',
  backgroundColor: 'white',
  display: 'flex',
}));

const content = css((theme) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 2,

  [`@media (max-width: ${theme.breakpoints.medium})`]: {
    padding: '24px',
  },
}));

const cardContent = css(() => ({
  display: 'flex',
  justifyContent: 'center',
}));

const error = css((theme) => ({
  color: theme.palette.error.error,
  textAlign: 'center',
}));

const loginButton = css((theme) => ({
  width: '100%',
  backgroundColor: theme.palette.primary.primary,
  borderRadius: '100px',
  height: '40px',
}));
